import { clsx } from 'clsx';
import React, { FC, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClickOutside } from '../../hooks/useClickOutside';
import { setToLocalStorage } from '../../lib/localStorage';
import { Icons } from '../svg';
import styles from './styles.module.scss';

type TLanguage = {
  flag: () => JSX.Element;
  title: string;
  slug: string;
};

const LANGUAGES = [
  {
    flag: Icons.English,
    title: 'English',
    slug: 'en',
  },
  {
    flag: Icons.German,
    title: 'Deutsch',
    slug: 'de',
  },
  {
    flag: Icons.Russian,
    title: 'Русский',
    slug: 'ru',
  },
];

export const LanguageDropdown: FC = () => {
  const { i18n } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [currentLanguage, setCurrentLanguage] = useState(
    () => LANGUAGES.find((l) => l.slug === i18n.language) ?? LANGUAGES[0],
  );

  const { flag: Flag, title, slug } = currentLanguage;
  const filteredLanguages = useMemo(
    () => LANGUAGES.filter((i) => i.slug !== slug),
    [slug],
  );

  const onOpen = () => {
    setIsOpen(true);
  };

  const onClose = () => {
    setIsOpen(false);
  };

  const setLang = (lang: TLanguage) => {
    setCurrentLanguage(lang);
    setToLocalStorage('lang', lang.slug);
    i18n.changeLanguage(lang.slug);
    setIsOpen(false);
  };

  const onMouseEnter = () => {
    setIsActive(false);
  };

  const onMouseLeave = () => {
    setIsActive(true);
  };

  useClickOutside(ref, onClose);

  return (
    <div ref={ref} className={styles.container}>
      <div className={styles.current} onClick={onOpen}>
        <Flag />
        {title}
        <Icons.ChevronPrimary />
      </div>
      <ul
        className={clsx(styles.list, isOpen && styles.visible)}
        onMouseLeave={onMouseLeave}
      >
        <li
          className={clsx(styles.item, isActive && styles.active)}
          onClick={() => setLang(currentLanguage)}
          onMouseEnter={onMouseEnter}
        >
          <Flag />
          {title}
        </li>
        {filteredLanguages.map((lang) => {
          const { slug, title, flag: Flag } = lang;

          return (
            <li
              key={slug}
              className={styles.item}
              onClick={() => setLang(lang)}
            >
              <Flag />
              {title}
            </li>
          );
        })}
      </ul>
    </div>
  );
};
